import * as queryString from 'query-string';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';

import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { LegacySiteUserSubspaceName, legacySiteUserSubspaceNameToExplorableItemType, SiteUserSubspaceName } from '../../state/SpaceInfo';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteUserSpaceProvider, SiteUserSpaceProviderProps } from './SiteUserSpaceProvider';

// The properties here must match the strings in the Routes' path below
export interface SiteUserSpaceRouteParams {
  readonly domain: string;
  readonly username: string;
}
export interface SiteUserRouteComponentProps extends RouteComponentProps<SiteUserSpaceRouteParams> {}

export interface SiteUserSpaceRouterOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly provider: (subspaceName: SiteUserSubspaceName, providerProps: SiteUserSpaceComponentWithRouterProps) => (props: SiteUserRouteComponentProps) => JSX.Element;
}

export type SiteUserSpaceRouterProps = SiteUserSpaceRouterOwnProps;

export class SiteUserSpaceRouterComponent extends React.Component<SiteUserSpaceRouterProps> {
  private server: SessionManagementService;

  public constructor(props: SiteUserSpaceRouterProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      // Since the "/" is allowed in usernames historically back to early Tableau Server versions, we want to support
      // routing in these cases. However, by default, React Router doesn't handle this character in a way we want.
      // The "+" notation is a way to make the matching greedy, whereas without it we would break at all "/" characters,
      // which is the way React Router natively interprets matches.
      // See:
      // https://github.com/ReactTraining/react-router/issues/313
      // and
      // https://stackoverflow.com/questions/56133182/allow-slashes-in-route-parameter-on-react
      <Route path={decodeURIComponent(this.props.urls.userPath(':username+', ':domain'))} render={() => (
        <Switch>
          <Route exact
            path={decodeURIComponent(this.props.urls.userContentPath(':username+', ':domain'))}
            render={this.props.provider('content', this.props)}/>
          <Route exact
            path={decodeURIComponent(this.props.urls.userProjectsPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('projects')}/>
          <Route exact
            path={decodeURIComponent(this.props.urls.userWorkbooksPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('workbooks')}/>
          <Route exact
            path={decodeURIComponent(this.props.urls.userViewsPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('views')}/>
          {
            FeatureFlags.metricsSpaceEnabled &&
            <Route exact
              path={decodeURIComponent(this.props.urls.userMetricsPath(':username+', ':domain'))}
              render={this.redirectToContentSubspace('metrics')} />
          }
          <Route exact
            path={decodeURIComponent(this.props.urls.userDatasourcesPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('datasources')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userFlowsPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('flows')}/>
          <Route exact
            path={decodeURIComponent(this.props.urls.userDataAlertsPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'alerts')}/>
          <Route exact
            path={decodeURIComponent(this.props.urls.userSubscriptionsPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'subscriptions')}/>
          {
            FeatureFlags.collectionsEnabled &&
            <Route exact
              path={decodeURIComponent(this.props.urls.userCollectionsPath(':username+', ':domain'))}
              render={this.props.provider('collections', this.props)} />
          }
          <Route exact
            path={decodeURIComponent(this.props.urls.userSettingsPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'settings')}/>
          <Route exact path={decodeURIComponent(this.props.urls.userPath(':username+', ':domain'))} render={({ match }) => (
            <Redirect to={decodeURIComponent(this.props.urls.userContentPath(match.params.username, match.params.domain))} />
          )} />
          <Route render={this.error}/>
        </Switch>
      )} />
    );
  }

  private redirectToContentSubspace(specificContentType: LegacySiteUserSubspaceName): (props: SiteUserRouteComponentProps) => JSX.Element {
    return (props) => {
      let query = queryString.parse(props.location.search);
      query.contentType = legacySiteUserSubspaceNameToExplorableItemType(specificContentType);

      return <Redirect to={{
        pathname: this.props.urls.userContentPath(props.match.params.username, props.match.params.domain),
        search: queryString.stringify(query)
      }} />;
    };
  }

  private renderProtectedSubspace(server: SessionManagementService, subspace: SiteUserSubspaceName): (props: SiteUserRouteComponentProps) => JSX.Element {
    return (props) => {
      if (server.isUserAuthorized(AuthLevel.SelfOrAdmin, decodeURIComponent(props.match.params.username), props.match.params.domain)) {
        return this.props.provider(subspace, this.props)(props);
      }
      return this.error(props);
    };
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }
}

export type SiteUserSpaceComponentWithRouterProps = Omit<SiteUserSpaceRouterProps, 'provider'>;
export const provider = (subspaceName: SiteUserSubspaceName, providerProps: SiteUserSpaceComponentWithRouterProps): (props: SiteUserRouteComponentProps) => JSX.Element => {
  return (props: SiteUserRouteComponentProps): JSX.Element => {
    const spaceProps: SiteUserSpaceProviderProps = {
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      // Need to decode username as special characters like space, #, etc are allowed for an username and are decoded in the url
      userName: decodeURIComponent(props.match.params.username),
      domainName: props.match.params.domain,
      siteName: providerProps.siteName,
      subspaceName,
      urls: providerProps.urls,
    };
    return <SiteUserSpaceProvider {...spaceProps} />;
  };
};

type SiteUserSpaceComponentOwnProps = Omit<SiteUserSpaceRouterOwnProps, 'provider'>;
const SiteUserSpaceComponent = (props: SiteUserSpaceComponentWithRouterProps) => {
  return (
    <SiteUserSpaceRouterComponent {...props} provider={provider}/>
  );
};

export const SiteUserSpaceRouter: React.FunctionComponent<SiteUserSpaceComponentOwnProps> = SiteUserSpaceComponent;

